import React, { useState } from 'react';
import { analyticsAPI } from '../utils/api';

const PerformanceTracker = ({ contentId, onUpdate, onClose }) => {
  const [performanceData, setPerformanceData] = useState({
    views: '',
    likes: '',
    shares: '',
    comments: '',
    platform: 'instagram'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Calculate engagement rate
      const views = parseInt(performanceData.views) || 0;
      const likes = parseInt(performanceData.likes) || 0;
      const shares = parseInt(performanceData.shares) || 0;
      const comments = parseInt(performanceData.comments) || 0;
      
      const totalEngagements = likes + shares + comments;
      const engagementRate = views > 0 ? totalEngagements / views : 0;

      // Track the performance data
      await analyticsAPI.trackContent(contentId, {
        views,
        engagement_rate: engagementRate,
        platform: performanceData.platform,
        metrics: {
          likes,
          shares,
          comments,
          total_engagements: totalEngagements
        }
      });

      onUpdate && onUpdate();
      onClose && onClose();
    } catch (err) {
      console.error('Error tracking performance:', err);
      setError('Failed to track performance data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Track Performance</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform
              </label>
              <select
                value={performanceData.platform}
                onChange={(e) => setPerformanceData(prev => ({ ...prev, platform: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
              </select>
            </div>

            {/* Views */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Views
              </label>
              <input
                type="number"
                value={performanceData.views}
                onChange={(e) => setPerformanceData(prev => ({ ...prev, views: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                min="0"
                required
              />
            </div>

            {/* Likes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Likes
              </label>
              <input
                type="number"
                value={performanceData.likes}
                onChange={(e) => setPerformanceData(prev => ({ ...prev, likes: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Shares */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shares/Reposts
              </label>
              <input
                type="number"
                value={performanceData.shares}
                onChange={(e) => setPerformanceData(prev => ({ ...prev, shares: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments
              </label>
              <input
                type="number"
                value={performanceData.comments}
                onChange={(e) => setPerformanceData(prev => ({ ...prev, comments: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Tracking...' : 'Track Performance'}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Update this data regularly to get better AI insights about your content performance patterns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTracker; 